import { Component, inject, signal } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { TextareaComponent } from '../../../../shared/ui/forms/textarea/textarea.component';
import { LoadService } from '../../../../core/services/load.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';

@Component({
  selector: 'app-edit-notes-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    TextareaComponent
],
  templateUrl: './edit-notes-form.component.html',
})
export class EditNotesFormComponent {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);

  protected readonly load: Load = this.modalService.state()!.data.load;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      driverInstructions: [null],
      notes: [null],
    });

    // Initialize form with data from the load
    this.initializeForm();
  }

  private initializeForm(): void {
    this.form.patchValue(
      {
        driverInstructions: this.load.instructions || null,
        notes: this.load.notes || null,
      },
      { emitEvent: false } // Prevent infinite loops
    );
  }

  cancel(): void {
    this.modalService.close();
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      // Optionally mark all fields as touched to show errors
      this.form.markAllAsTouched();
      return;
    }

    // Set local saving state
    this.isSaving.set(true);

    // Show global loading overlay
    this.loadingService.show({
      variant: 'overlay',
      title: 'Updating Stop Details',
      subtitle: 'Saving your changes...',
    });

    // Map form value to a partial Load object for updating
    const formValue = this.form.getRawValue();
    const updatedData: Partial<Load> = {
      instructions: formValue.driverInstructions,
      notes: formValue.notes,
    };

    this.loadService.updateLoad(this.load.id, updatedData).subscribe({
      next: () => {
        this.loadingService.hide();
        this.modalService.close();
        this.onSuccess?.();
        setTimeout(() => {
          this.toastService.show('success', 'Notes updated successfully', {
            title: 'Success',
            duration: 4000,
          });
        }, 100);
      },
      error: (_error) => {
        this.loadingService.hide();
        this.toastService.show(
          'error',
          'Failed to update notes. Please try again.',
          {
            title: 'Update Failed',
            showClose: true,
            duration: 0,
          }
        );
      },
      complete: () => {
        this.isSaving.set(false);
      }
    });
  }
}
